import { Dashboard, UserNextBestActions } from '@platform/client';
import {
  DatePicker,
  InputDropdown,
  InputDropdownItem,
  Modal,
  RadioGroup,
  RadioItem,
} from '@platform/components';
import { useQueryClient } from '@tanstack/react-query';
import cx from 'classix';
import {
  GetExperteerPreferencesResponseReason,
  SearchStatusEnum,
} from '@platform/client/schemas';
import mixpanel from 'mixpanel-browser';
import { useEffect, useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import EVENTS from '../../analytics/events';
import dataTestids from '../../constants/test-ids';

const searchStatusLabels: Record<SearchStatusEnum, string> = {
  'Active Team': 'Active - Team',
  'Active Seeking': 'Actively Seeking',
  'Open to Intro': 'Open to Intro',
  'Not Seeking': 'Not seeking',
};

const statusRadioItems: RadioItem[] = [
  {
    title: 'Actively Seeking',
    description: "I'm actively searching for an experteering project",
    value: 'Active Seeking',
  },
  {
    title: 'Open to introductions',
    description:
      "I'm not actively searching, but open to introductions from interested organizations",
    value: 'Open to Intro',
  },
  {
    title: 'Not seeking',
    description: "I'm not searching at this time",
    value: 'Not Seeking',
  },
];

/** While the API can return null for the "reason" why someone isn't seeking a match,
 * this is only for historical reasons and should be non-null in practice */
type NotSeekingReasons = NonNullable<GetExperteerPreferencesResponseReason>;

const notSeekingDropdownItems: InputDropdownItem[] = [
  {
    text: `I'm already matched to a project`,
    textValue: 'Matched',
  },
  {
    text: `I'm opting out`,
    textValue: 'Opted Out',
  },
  {
    text: `I'm postponing`,
    textValue: 'Postponed',
  },
];

/** The user's project search status, including the model to update their status */
const SearchStatusIndicator = () => {
  const queryClient = useQueryClient();
  const experteerPreferences = Dashboard.useGetExperteerPreferences();
  const [showChangeStatusModal, setShowChangeStatusModal] = useState(false);
  const [newSearchStatusDraft, setNewSearchStatusDraft] =
    useState<SearchStatusEnum>('Active Seeking');
  const [selectedReasonDraft, setSelectedReasonDraft] = useState<NotSeekingReasons>('Matched');
  const [selectedDateDraft, setSelectedDateDraft] = useState(new Date());

  // Update the draft when the status is loaded from the API and when it's refetched
  useEffect(() => {
    setNewSearchStatusDraft(experteerPreferences.data?.searchStatus ?? 'Active Seeking');
    const reason = experteerPreferences.data?.reason;
    setSelectedReasonDraft(reason ?? 'Matched');
    // Note addition of 'T00:00:00' to ensure the date does not include any hour offset through automatic conversion from UTC
    const initialDateDraft =
      experteerPreferences.data?.earliestDateAvailable ?
        new Date(`${experteerPreferences.data?.earliestDateAvailable}T00:00:00`)
        : new Date();

    setSelectedDateDraft(initialDateDraft);
  }, [
    experteerPreferences.data?.earliestDateAvailable,
    experteerPreferences.data?.reason,
    experteerPreferences.data?.searchStatus,
  ]);

  const searchStatusMutation = Dashboard.useUpdateSearchStatus({
    mutation: {
      onError: () => {
        // TODO: Ability to show an in-app error when the status set fails
        // Revert the eager update
        experteerPreferences.refetch();
      },
      // Some NBAs are conditional to search status, we want to recalculate on change
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: UserNextBestActions.getGetUserNextBestActionsQueryKey(),
        });
      },
    },
  });

  const setDateDraft = (date: Date) => {
    setSelectedDateDraft(date);
  };

  const onModalConfirm = () => {
    // Optimistic update. This was written before I read
    // https://tanstack.com/query/latest/docs/framework/react/guides/optimistic-updates#via-the-ui, so this can likely
    // be simplified
    queryClient.setQueryData(experteerPreferences.queryKey, (old) => ({
      earliestDateAvailable: old?.earliestDateAvailable ?? null,
      reason: old?.reason ?? null,
      searchStatus: newSearchStatusDraft,
    }));

    const isoDate = selectedDateDraft.toISOString();

    // The data is shaped slightly differently based on the questions asked for the new status
    if (newSearchStatusDraft === 'Not Seeking') {
      searchStatusMutation.mutate({
        data:
          selectedReasonDraft === 'Postponed' ?
            {
              searchStatus: newSearchStatusDraft,
              reason: selectedReasonDraft,
              earliestDateAvailable: isoDate,
            }
            : {
              searchStatus: newSearchStatusDraft,
              reason: selectedReasonDraft,
            },
      });
    } else {
      searchStatusMutation.mutate({
        data: {
          searchStatus: newSearchStatusDraft,
          earliestDateAvailable: isoDate,
        },
      });
    }

    setShowChangeStatusModal(false);
    mixpanel.track(EVENTS.DASHBOARD.HOME.SEARCH_STATUS_MODAL_CONFIRMED, {
      oldSearchStatus: experteerPreferences.data?.searchStatus,
      newSearchStatus: newSearchStatusDraft,
    });
  };

  const onModalCancel = () => {
    if (!experteerPreferences.data) {
      return;
    }

    setNewSearchStatusDraft(experteerPreferences.data.searchStatus);
    setShowChangeStatusModal(false);
    mixpanel.track(EVENTS.DASHBOARD.HOME.SEARCH_STATUS_MODAL_CANCELLED, {
      searchStatus: experteerPreferences.data.searchStatus,
    });
  };

  return (
    <div className='flex gap-2 text-sm'>
      {experteerPreferences.isSuccess && experteerPreferences.data?.searchStatus ?
        <>
          <span className='flex items-center gap-[5px] py-[2px] px-[6px] bg-white border-gray-200 border-[1px] rounded-md z-0'>
            {/* Ugly replication of the non-icon set approach to the duotone circle from
        https://bitbucket.org/movingworlds/movingworlds/pull-requests/1095#chg_movingworlds/assets/scss/components/_status-badge.scss_newline35 */}
            <div
              className={cx(
                'w-[6px] h-[6px] rounded-full',
                experteerPreferences.data?.searchStatus !== 'Not Seeking' &&
                "bg-success-500 relative after:content-[''] after:w-[12px] after:h-[12px] after:bg-success-25 after:rounded-full after:absolute after:z-[-1] after:left-[-3px] after:top-[-3px]",
                experteerPreferences.data?.searchStatus === 'Not Seeking' && 'bg-gray-400',
              )}
            />
            <span>{searchStatusLabels[experteerPreferences.data.searchStatus]}</span>
          </span>
          <button
            className='blue-link'
            onClick={() => {
              setShowChangeStatusModal(true);
              mixpanel.track(EVENTS.DASHBOARD.HOME.CHANGE_YOUR_STATUS_CLICKED, {
                searchStatus: experteerPreferences.data.searchStatus,
              });
            }}
          >
            Change your status
          </button>
        </>
        : <Skeleton width={280} height={23} />}

      <Modal
        variant='dialog'
        headerDivision='divider'
        title='Search Status'
        isOpen={showChangeStatusModal}
        onClose={() => setShowChangeStatusModal(false)}
        primaryCTA={{
          text: 'Confirm',
          callback: onModalConfirm,
          isLoading: searchStatusMutation.isPending,
        }}
        secondaryCTA={{
          text: 'Cancel',
          callback: onModalCancel,
        }}
      >
        <div className='flex flex-col gap-4'>
          <RadioGroup
            name='searchStatusRadioGroup'
            aria-label='Search status selection'
            defaultValue={newSearchStatusDraft}
            onChange={(value) => setNewSearchStatusDraft(value as SearchStatusEnum)}
            items={statusRadioItems}
            borderless
          />
          <div>
            <div className='text-md mb-2'>
              {newSearchStatusDraft !== 'Not Seeking' && 'Earliest Date Available:'}
            </div>
            {newSearchStatusDraft !== 'Not Seeking' && (
              <DatePicker
                initialDate={selectedDateDraft}
                onChange={(newDate) => setDateDraft(newDate)}
              />
            )}
            {newSearchStatusDraft === 'Not Seeking' && (
              <>
                <div>
                  <InputDropdown
                    label='Reason:'
                    type='default'
                    items={notSeekingDropdownItems}
                    inputDisabled
                    onSelectionChange={(key) => setSelectedReasonDraft(key as NotSeekingReasons)}
                  />
                </div>
                {selectedReasonDraft === 'Postponed' && (
                  <div data-testid={dataTestids.DASHBOARD.SEARCH_STATUS.POSTPONED_DATEPICKER}>
                    <div className='text-md mt-4 mb-2'>Search Postponed Until:</div>
                    <DatePicker
                      initialDate={selectedDateDraft}
                      onChange={(newDate) => {
                        setDateDraft(newDate);
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default SearchStatusIndicator;
